import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Wrench,
  SignOut,
  Clock,
  CheckCircle,
  Circle,
  Warning,
  MapPin,
  Calendar,
  CaretRight,
  ArrowClockwise
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

interface OrderIssue {
  id?: string;
  title?: string;
  urgency_level?: number;
}

interface OrderProperty {
  id?: string;
  name?: string;
}

interface Order {
  id: string;
  status?: string;
  instructions?: string;
  due_date?: string;
  created_at?: string;
  issue?: OrderIssue;
  property?: OrderProperty;
}

export default function SubcontractorOrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const email = localStorage.getItem('subcontractor_email');

  useEffect(() => {
    const token = localStorage.getItem('subcontractor_token');
    if (!token) {
      navigate('/subcontractor/login');
      return;
    }
    fetchOrders();
  }, [navigate]);

  const fetchOrders = async () => {
    const token = localStorage.getItem('subcontractor_token');
    try {
      const res = await fetch(`${API_URL}/api/subcontractor/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.status === 401) {
        localStorage.removeItem('subcontractor_token');
        localStorage.removeItem('subcontractor_email');
        navigate('/subcontractor/login');
        return;
      }

      if (res.ok) {
        const data = await res.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('subcontractor_token');
    localStorage.removeItem('subcontractor_email');
    navigate('/subcontractor/login');
    toast.success('Utloggad');
  };

  const getStatusBadge = (status: string | undefined) => {
    const badges: Record<string, { label: string; bg: string; text: string; icon: typeof Clock }> = {
      pending: { label: 'Väntande', bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
      accepted: { label: 'Accepterad', bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle },
      in_progress: { label: 'Pågår', bg: 'bg-purple-100', text: 'text-purple-700', icon: ArrowClockwise },
      completed: { label: 'Slutförd', bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      declined: { label: 'Avböjd', bg: 'bg-red-100', text: 'text-red-700', icon: Warning }
    };
    return badges[status || 'pending'] || badges.pending;
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'active') return ['pending', 'accepted', 'in_progress'].includes(order.status || '');
    if (filter === 'completed') return order.status === 'completed';
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subcontractor-orders-page">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
              <Wrench size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-slate-900">Leverantörsportal</h1>
              <p className="text-xs text-slate-500">{email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="subcontractor-logout">
            <SignOut size={18} className="mr-2" />
            Logga ut
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { key: 'all', label: 'Alla' },
            { key: 'active', label: 'Aktiva' },
            { key: 'completed', label: 'Slutförda' }
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                filter === f.key 
                  ? 'bg-slate-900 text-white' 
                  : 'bg-white border border-border text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl border border-border p-5 animate-pulse">
                <div className="h-5 w-3/4 bg-slate-200 rounded mb-3" />
                <div className="h-4 w-1/2 bg-slate-200 rounded mb-2" />
                <div className="h-4 w-1/3 bg-slate-200 rounded" />
              </div>
            ))}
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-white rounded-xl border border-border p-12 text-center">
            <Wrench size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">
              {filter === 'all' ? 'Inga ordrar ännu' : 'Inga ordrar i denna kategori'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map(order => {
              const status = getStatusBadge(order.status);
              const StatusIcon = status.icon;
              
              return (
                <Link
                  key={order.id}
                  to={`/subcontractor/orders/${order.id}`}
                  className="block bg-white rounded-xl border border-border p-5 hover:shadow-md transition-shadow"
                  data-testid={`order-card-${order.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                          <StatusIcon size={14} />
                          {status.label}
                        </span>
                        {(order.issue?.urgency_level ?? 0) >= 4 && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <Warning size={12} />
                            Akut
                          </span>
                        )}
                      </div>
                      
                      <h3 className="font-medium text-slate-900 mb-1 truncate">
                        {order.issue?.title || 'Arbetsorder'}
                      </h3>
                      
                      <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                        {order.instructions}
                      </p>
                      
                      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                        {order.property && (
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {order.property.name}
                          </span>
                        )}
                        {order.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {new Date(order.due_date).toLocaleDateString()}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                        </span>
                      </div>
                    </div>
                    
                    <CaretRight size={20} className="text-slate-400 flex-shrink-0" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
